import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environment';
import { BrandType } from '@ess-front/shared/models';
import { TalkJsUserRole } from '@app/utils/models/enum/talkjs-user-role.enum';
import { MemberExperienceManager } from '@app/utils/models/user/member-experience-manager.model';
import { MemberExperienceManagerBE, UserBE } from '@app/utils/models/user/user-be.model';
import { MemberProfileFormValue } from '@app/utils/models/user/member-profile-form-value.model';
import { MemberUser } from '@app/utils/models/user/member-user.model';
import { UserPreferencesBE } from '@app/utils/models/user/user-preferences-be.model';
import { UserPreferences } from '@app/utils/models/user/user-preferences.model';
import { User } from '@app/utils/models/user/user.model';
import { UserUpdateBE } from '@app/utils/models/user/user-update-be.model';
import { toIsoDate } from '@app/utils/functions/to-iso-date';
import { catchFieldsError } from '@app/utils/functions/catch-fields-error';
import { forkJoin, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserApiService {
  private readonly apiURL = `${environment.apiEnv}api/`;
  // A 400 on `PATCH users/me/` keys its errors by these backend fields, plus `avatar` for the photo sent
  // alongside them. Anything else (`non_field_errors`, ...) has no input to show it on: dropped when the
  // 400 also names one of these fields, otherwise the error is passed through for a snackbar.
  private readonly USER_UPDATE_FIELD_BY_BE_KEY: Record<keyof UserUpdateBE | 'avatar', keyof MemberProfileFormValue> = {
    avatar: 'avatarFile',
    birth_date: 'birthDate',
    country: 'countryId',
    first_name: 'firstName',
    last_name: 'lastName',
    middle_name: 'middleName',
    passport_expiry_date: 'passportExpiryDate',
    passport_number: 'passportNumber',
    phone: 'phone',
    phone_country_code: 'phoneCountryCode',
  };

  // DI
  private readonly httpClient = inject(HttpClient);

  getUserFromStorage$(): Observable<User> {
    const url = `${this.apiURL}users/me/`;
    return forkJoin([this.httpClient.get<UserBE>(url), this.getUserPreferences$()]).pipe(
      map(([userBE, userPreferences]) => ({ ...this.transformIntoUser(userBE), userPreferences })),
    );
  }

  getMemberUserByHash$(hash: string): Observable<MemberUser> {
    const url = `${this.apiURL}v2/users/${hash}/`;
    return this.httpClient.get<UserBE>(url).pipe(map(userBE => this.transformIntoMemberUser(userBE)));
  }

  // A multipart PATCH: `''` clears a field, and the photo travels in the same request.
  // Returns the user without preferences: the PATCH response carries none.
  updateUser$(memberProfileFormValue: MemberProfileFormValue): Observable<Omit<User, 'userPreferences'>> {
    return this.httpClient
      .patch<UserBE>(`${this.apiURL}users/me/`, this.transformIntoUserUpdateFormData(memberProfileFormValue))
      .pipe(
        map(userBE => this.transformIntoUser(userBE)),
        catchFieldsError<Omit<User, 'userPreferences'>, MemberProfileFormValue>(this.USER_UPDATE_FIELD_BY_BE_KEY),
      );
  }

  private getUserPreferences$(): Observable<UserPreferences> {
    const url = `${this.apiURL}users/me/preferences/`;
    return this.httpClient
      .get<UserPreferencesBE>(url)
      .pipe(map(userPreferences => this.transformIntoUserPreferences(userPreferences)));
  }

  private transformIntoMemberUser(user: UserBE): MemberUser {
    return {
      avatar: user.avatar,
      firstName: user.first_name,
      lastName: user.last_name,
    };
  }

  // Preferences come from their own endpoint, so `getUserFromStorage$` adds them.
  private transformIntoUser(userBE: UserBE): Omit<User, 'userPreferences'> {
    return {
      availableCredit: userBE.available_credit,
      avatar: userBE.avatar,
      birthDate: userBE.birth_date,
      brand: userBE.brand || BrandType.ESSENTIALIST,
      categories: userBE.categories,
      chatSignature: userBE.chat_signature,
      city: userBE.city,
      countryId: userBE.country_id,
      email: userBE.email,
      firstName: userBE.first_name,
      hash: userBE.hash,
      hasToShowOnboarding: !userBE.is_onboarding_done,
      id: userBE.id,
      language: userBE.preferred_language,
      lastName: userBE.last_name,
      memberExperienceManager:
        userBE.member_experience_manager && this.transformIntoMemberExperienceManager(userBE.member_experience_manager),
      memberSince: userBE.member_since,
      memConversationId: userBE.mem_conversation_id ?? null,
      middleName: userBE.middle_name,
      name: `${userBE.first_name} ${userBE.last_name}`.trim(),
      passportExpiryDate: userBE.passport_expiry_date,
      passportNumber: userBE.passport_number,
      phone: userBE.phone,
      phoneCountryCode: userBE.phone_country_code,
      preferences: userBE.preferences,
      talkJsRole: TalkJsUserRole.USER,
    };
  }

  // The photo goes only when one was picked, so a save without a new photo keeps the current one.
  private transformIntoUserUpdateFormData(memberProfileFormValue: MemberProfileFormValue): FormData {
    const userUpdateBE: UserUpdateBE = {
      birth_date: toIsoDate(memberProfileFormValue.birthDate) ?? '',
      country: memberProfileFormValue.countryId === null ? '' : String(memberProfileFormValue.countryId),
      first_name: memberProfileFormValue.firstName,
      last_name: memberProfileFormValue.lastName,
      middle_name: memberProfileFormValue.middleName,
      passport_expiry_date: toIsoDate(memberProfileFormValue.passportExpiryDate) ?? '',
      passport_number: memberProfileFormValue.passportNumber,
      phone: memberProfileFormValue.phone,
      phone_country_code: memberProfileFormValue.phoneCountryCode ?? '',
    };

    const formData = new FormData();
    Object.entries(userUpdateBE).forEach(([key, value]) => formData.set(key, value));
    if (memberProfileFormValue.avatarFile) {
      formData.set('avatar', memberProfileFormValue.avatarFile);
    }
    return formData;
  }

  private transformIntoMemberExperienceManager(member: MemberExperienceManagerBE): MemberExperienceManager {
    return {
      avatar: member.avatar,
      calendarUrl: member.calendar_url,
      email: member.email,
      firstName: member.first_name,
      lastName: member.last_name,
      name: member.name,
    };
  }

  private transformIntoUserPreferences({
    budget_per_night_range,
    currency,
    preferred_airport,
  }: UserPreferencesBE): UserPreferences {
    return {
      preferredAirport: preferred_airport,
      budgetPerNightRange: {
        max: budget_per_night_range.max_budget_per_night,
        min: budget_per_night_range.min_budget_per_night,
      },
      currency: {
        code: currency.alphabetic_code,
        symbol: currency.symbol,
      },
    };
  }
}
